import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from '@xstate/store/react';
import { canvasSelectionStore } from '../stores/canvasSelectionStore';
import { isFocusInEditable } from '../../lib/domUtils';
import { useCanvasStores } from '../stores/CanvasContext';

export interface UseCanvasMultiSelectResult {
  selectedNodeIds: Set<string>;
  selectNode: (taskId: string, additive: boolean) => void;
  selectionCount: number;
}

export function useCanvasMultiSelect(): UseCanvasMultiSelectResult {
  const { store, uiStore, txManager } = useCanvasStores();

  const selectedIdsArray = useSelector(canvasSelectionStore, s => s.context.selectedEntityIds);
  const selectedNodeIds = useMemo(() => new Set(selectedIdsArray), [selectedIdsArray]);

  const selectNode = useCallback((taskId: string, additive: boolean) => {
    if (additive) {
      canvasSelectionStore.send({ type: 'entity.toggle', entityId: taskId });
    } else {
      canvasSelectionStore.send({ type: 'entity.selectOne', entityId: taskId });
    }
  }, []);

  const handleRemoveSelected = useCallback(() => {
    const sel = canvasSelectionStore.getSnapshot().context;
    txManager.begin('delete-selection');
    for (const entityId of sel.selectedEntityIds) {
      store.send({ type: 'entity.remove', entityId });
    }
    txManager.commit();
    canvasSelectionStore.send({ type: 'entity.clear' });
  }, [store, txManager]);

  // Keyboard: Escape clears, Delete/Backspace removes, Ctrl+A selects all
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (isFocusInEditable()) return;

      if (e.key === 'Escape') {
        const uiCtx = uiStore.getSnapshot().context;
        if (uiCtx.depExplorerActive) {
          if (uiCtx.depExplorerFocusId !== null) {
            uiStore.send({ type: 'depExplorer.setFocus', entityId: null });
          } else {
            uiStore.send({ type: 'depExplorer.off' });
          }
          return;
        }
        const ctx = canvasSelectionStore.getSnapshot().context;
        if (ctx.enteredGroupId) {
          canvasSelectionStore.send({ type: 'group.exit' });
        } else {
          canvasSelectionStore.send({ type: 'entity.clear' });
        }
        return;
      }

      if ((e.key === 'Delete' || e.key === 'Backspace') && handleRemoveSelected) {
        const ctx = canvasSelectionStore.getSnapshot().context;
        if (ctx.selectedEntityIds.length > 0) {
          e.preventDefault();
          handleRemoveSelected();
        }
        return;
      }

      if (e.key === 'a' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        const ctx = store.getSnapshot().context;
        const raw = Object.values(ctx.nodes);
        const layers = ctx.layers;
        const lockedLayerIds = new Set(layers.filter(l => l.locked).map(l => l.id));
        const taskIds = raw.filter(ent => ent.kind === 'task' && !lockedLayerIds.has(ent.layerId)).map(ent => ent.id);
        canvasSelectionStore.send({ type: 'entity.set', entityIds: taskIds });
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleRemoveSelected, store, uiStore]);

  return {
    selectedNodeIds,
    selectNode,
    selectionCount: selectedIdsArray.length,
  };
}
